import { FC, useState } from "react";
import { useAtomValue } from "jotai";

import { CleanupTypeEnum, TaskDriverStatusEnum } from "@/domain/models/common";
import { TasksType } from "@/domain/models/job";

import { useUpdateServiceMutation } from "../data/mutations/serviceMutations";
import { useUpdateTaskMutation } from "../data/mutations/taskMutations";
import { useTaskQuery } from "../data/queries/taskQueries";
import { JobServiceTaskBaseType } from "../domain/task";
import { isDriverReviewingTaskAtom } from "../stores/atoms";
import { getCleanupTypeOptions } from "../utils/job";
import { checkTaskHasCleanupType } from "../utils/task";
import { DriverScreenWithSelectBox } from "./DriverScreenWithSelectBox";

const CLEANUP_TYPE_OPTIONS = getCleanupTypeOptions(true);

const DriverFillingInteriorCleanupType: FC<JobServiceTaskBaseType> = ({
  jobId,
  serviceId,
  taskId,
}) => {
  const isDriverReviewingTask = useAtomValue(isDriverReviewingTaskAtom);

  const { data: taskData } = useTaskQuery({ jobId, serviceId, taskId });

  const [selectedInteriorCleanupType, setSelectedInteriorCleanupType] =
    useState<CleanupTypeEnum>(taskData?.cleanupType ?? CleanupTypeEnum.NONE);

  const {
    mutate: updateService,
    isPending: isUpdatingService,
    variables,
  } = useUpdateServiceMutation();
  const { mutate: updateTask, isPending: isUpdatingTask } =
    useUpdateTaskMutation();

  const getNextStep = () => {
    if (isDriverReviewingTask) {
      return TaskDriverStatusEnum.TASK_REVIEW;
    }

    // For "None" cleanup type, go directly to TAKING_PHOTOS_AREAS_CLEANED
    if (selectedInteriorCleanupType === CleanupTypeEnum.NONE) {
      return TaskDriverStatusEnum.TAKING_PHOTOS_AREAS_CLEANED;
    }

    // For "Full Detail" cleanup type, go to FILLING_SQFT_CLEANED
    if (selectedInteriorCleanupType === CleanupTypeEnum.FULLDETAIL) {
      return TaskDriverStatusEnum.FILLING_SQFT_CLEANED;
    }

    // For "Rough Sweep" and "Fine Sweep", go to FILLING_FLOORS_CLEANED
    return TaskDriverStatusEnum.FILLING_FLOORS_CLEANED;
  };

  const onNextClick = () => {
    updateService({
      jobId,
      serviceId,
      driverFlowStatus: getNextStep(),
    });

    const taskParams: Partial<TasksType> = {
      jobId,
      serviceId,
      taskId,
      driverFlowStatus: getNextStep(),
    };

    // If current task's cleanup type is none, do not need to update unless the selected cleanup type is not none
    if (
      taskData?.cleanupType !== CleanupTypeEnum.NONE ||
      (taskData.cleanupType === CleanupTypeEnum.NONE &&
        selectedInteriorCleanupType !== CleanupTypeEnum.NONE)
    ) {
      taskParams.cleanupType = selectedInteriorCleanupType;
    }

    updateTask(taskParams);
  };

  const isLoading = isUpdatingService || isUpdatingTask;

  const isNextButtonLoading =
    isLoading &&
    variables?.driverFlowStatus === TaskDriverStatusEnum.FILLING_SQFT_CLEANED;
  const isPreviousButtonLoading =
    isLoading &&
    variables?.driverFlowStatus ===
      TaskDriverStatusEnum.ASKING_FOR_ANOTHER_LOAD;

  const goToPreviousStep = () => {
    updateService({
      jobId,
      serviceId,
      driverFlowStatus: TaskDriverStatusEnum.ASKING_FOR_ANOTHER_LOAD,
    });

    updateTask({
      jobId,
      serviceId,
      taskId,
      driverFlowStatus: TaskDriverStatusEnum.ASKING_FOR_ANOTHER_LOAD,
    });
  };

  const handleOnSelect = (option: string) => {
    setSelectedInteriorCleanupType(option as CleanupTypeEnum);
  };

  return (
    <DriverScreenWithSelectBox
      jobId={jobId}
      serviceId={serviceId}
      taskId={taskId}
      title="driver_filling_interior_cleanup_type.title"
      selectBoxProps={{
        items: CLEANUP_TYPE_OPTIONS,
        selectedValue: selectedInteriorCleanupType,
        onSelect: handleOnSelect,
      }}
      bottomButtonProps={{
        isNextButtonLoading,
        nextButtonTitle: isDriverReviewingTask ? "review" : "next",
        onNextButtonClick: onNextClick,
        isNextButtonDisabled: !selectedInteriorCleanupType,
        previousButtonTitle: "driver_filling_interior_cleanup_type.back_button",
        onPreviousButtonClick: isDriverReviewingTask
          ? undefined
          : () => goToPreviousStep(),
        isPreviousButtonLoading,
      }}
    />
  );
};

export { DriverFillingInteriorCleanupType };
